import React, { useEffect, useMemo, useState } from 'react'
import { Col, Modal, Row } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import InputField from '@components/common/InputField/InputField.component'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchAllPatients } from '@store/actions/patientmanagement.action'
import { Patient } from '@dataTypes/PatientInfo.types'
import { getFullName } from '@utils/fullname.util'
import { calculateAge } from '@utils/dob.utils'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { TextAlign } from '@constants/Common/GridComponent.contants'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { RowSelectEventArgs } from '@syncfusion/ej2-react-grids'
import './AdvancePatientSearchStyle.css'

type AdvancePatientSearchProps = {
  isOpen: boolean
  toggleModal: () => void
  onSelectPatient?: (patient: Patient) => void
}

type SearchFilters = {
  firstName: string
  lastName: string
  age: string
  street: string
  cityState: string
  phone: string
  email: string
  householdFirstName: string
  householdLastName: string
}

const EMPTY_FILTERS: SearchFilters = {
  firstName: '',
  lastName: '',
  age: '',
  street: '',
  cityState: '',
  phone: '',
  email: '',
  householdFirstName: '',
  householdLastName: '',
}

const AGE_BUTTONS = [
  { label: '18-25', value: '18-25' },
  { label: '26-39', value: '26-39' },
  { label: '40-49', value: '40-49' },
  { label: '50-59', value: '50-59' },
  { label: '60+', value: '60+' },
]

const LOADING_CONTAINER = 'body'

const includesQuery = (value: string | undefined, query: string): boolean => {
  if (!query) return true
  return (value ?? '').toLowerCase().includes(query.toLowerCase())
}

const getHouseholdNames = (patient: Patient): string => {
  const relatives = Array.isArray(patient.relatives)
    ? patient.relatives
        .map((relative) => relative?.family_member_name ?? '')
        .filter(Boolean)
    : []
  const familyMembers = Array.isArray(patient.family_members_info)
    ? patient.family_members_info
        .map((member) => getFullName(member?.patient_member_info, true))
        .filter(Boolean)
    : []

  return [...relatives, ...familyMembers, patient.family_members ?? '']
    .filter(Boolean)
    .join(' ')
}

const matchesAgeRange = (patient: Patient, ageRange: string): boolean => {
  if (!ageRange || !patient.date_of_birth) return !ageRange

  const age = calculateAge(patient.date_of_birth)
  if (!Number.isFinite(age) || age < 0) return false

  if (ageRange === '60+') return age >= 60

  const [min, max] = ageRange.split('-').map(Number)
  return age >= min && age <= max
}

const formatPatientAddress = (patient: Patient): string =>
  [patient.address, patient.city, patient.state, patient.zip]
    .filter((part) => part && String(part).trim())
    .join(', ')

const AdvancePatientSearch: React.FC<AdvancePatientSearchProps> = ({
  isOpen,
  toggleModal,
  onSelectPatient,
}) => {
  const dispatch = useAppDispatch()
  const patientsState = useAppSelector((state) => state.patient.data) || []
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [fetchDone, setFetchDone] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setFetchDone(false)
      setFilters(EMPTY_FILTERS)
      setSelectedPatient(null)
      hideLoading(LOADING_CONTAINER)
      return
    }

    showLoading({ container: LOADING_CONTAINER })
    void dispatch(fetchAllPatients()).finally(() => {
      setFetchDone(true)
      hideLoading(LOADING_CONTAINER)
    })
  }, [isOpen, dispatch, showLoading, hideLoading])

  const updateFilter =
    (key: keyof SearchFilters) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setFilters((previous) => ({
        ...previous,
        [key]: event.target.value,
      }))
    }

  const handleAgeSelect = (age: string): void => {
    setFilters((previous) => ({
      ...previous,
      age: previous.age === age ? '' : age,
    }))
  }

  const filteredPatients = useMemo((): Patient[] => {
    if (!Array.isArray(patientsState)) return []

    return patientsState.filter((patient) => {
      const household = getHouseholdNames(patient)
      const phoneDigits = [
        patient.cellphone,
        patient.home_phone,
        patient.work_phone,
      ]
        .filter(Boolean)
        .join(' ')
        .replace(/\D/g, '')
      const phoneQuery = filters.phone.trim().replace(/\D/g, '')

      return (
        includesQuery(patient.first_name, filters.firstName.trim()) &&
        includesQuery(patient.last_name, filters.lastName.trim()) &&
        matchesAgeRange(patient, filters.age) &&
        includesQuery(patient.address, filters.street.trim()) &&
        (includesQuery(patient.city, filters.cityState.trim()) ||
          includesQuery(patient.state, filters.cityState.trim())) &&
        includesQuery(phoneDigits, phoneQuery) &&
        includesQuery(patient.email, filters.email.trim()) &&
        includesQuery(household, filters.householdFirstName.trim()) &&
        includesQuery(household, filters.householdLastName.trim())
      )
    })
  }, [patientsState, filters])

  const columns = useMemo(
    () => [
      {
        headerText: 'Patient name',
        field: 'first_name',
        width: '140px',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        template: (props: Patient) => (
          <div className="d-flex align-items-center">
            {getFullName(props, false, isHIPAAView)}
          </div>
        ),
      },
      {
        headerText: 'Age',
        field: 'date_of_birth',
        width: '50px',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        template: (props: Patient) => (
          <>{props.date_of_birth ? calculateAge(props.date_of_birth) : '—'}</>
        ),
      },
      {
        headerText: 'Address',
        field: 'address',
        width: '160px',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        template: (props: Patient) => <>{formatPatientAddress(props) || '—'}</>,
      },
      {
        headerText: 'Phone',
        field: 'cellphone',
        width: '110px',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        template: (props: Patient) => (
          <>
            {props.cellphone ? formatPhoneNumber(props.cellphone, false) : '—'}
          </>
        ),
      },
      {
        headerText: 'Email',
        field: 'email',
        width: '140px',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        template: (props: Patient) => <>{props.email || '—'}</>,
      },
    ],
    [isHIPAAView],
  )

  const handleClose = (): void => {
    setFilters(EMPTY_FILTERS)
    setSelectedPatient(null)
    toggleModal()
  }

  const handleReset = (): void => {
    setFilters(EMPTY_FILTERS)
    setSelectedPatient(null)
  }

  const confirmSelection = (patient: Patient): void => {
    onSelectPatient?.(patient)
    handleClose()
  }

  const onRowSelect = (args: RowSelectEventArgs): void => {
    if (args?.data && typeof args.data === 'object') {
      setSelectedPatient(args.data as Patient)
    }
  }

  const handleRowDoubleClick = (args: { rowData?: Patient }): void => {
    const patient = args?.rowData ?? selectedPatient
    if (patient) {
      confirmSelection(patient)
    }
  }

  const showModal = isOpen && fetchDone

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="lg"
      className="advance-patient-search-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="bg-custom3" closeButton>
        <Modal.Title>Advanced patient search</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="aps-fields">
          <InputField
            label="First name"
            value={filters.firstName}
            onChange={updateFilter('firstName')}
          />
          <InputField
            label="Last name"
            value={filters.lastName}
            onChange={updateFilter('lastName')}
          />
          <div className="aps-field-full">
            <span className="input-field-label">Age</span>
            <ButtonGroup
              buttons={AGE_BUTTONS}
              activeTab={filters.age}
              onButtonClick={handleAgeSelect}
              className="aps-age-tabs"
            />
          </div>
          <InputField
            label="Street"
            value={filters.street}
            onChange={updateFilter('street')}
          />
          <InputField
            label="City/state"
            value={filters.cityState}
            onChange={updateFilter('cityState')}
          />
          <InputField
            label="Phone"
            value={filters.phone}
            onChange={updateFilter('phone')}
          />
          <InputField
            label="Email"
            value={filters.email}
            onChange={updateFilter('email')}
          />
          <InputField
            label="Household first name"
            value={filters.householdFirstName}
            onChange={updateFilter('householdFirstName')}
          />
          <InputField
            label="Household last name"
            value={filters.householdLastName}
            onChange={updateFilter('householdLastName')}
          />
        </div>

        <Row className="mt-3">
          <Col>
            <div className="aps-grid-container">
              <DataGridComponent
                data={filteredPatients}
                columns={columns}
                rowHeight={36}
                allowTextWrap={true}
                rowSelected={onRowSelect}
                onRowDoubleClick={handleRowDoubleClick}
                allowPaging={true}
                sortSettings={{
                  columns: [{ field: 'first_name', direction: 'Ascending' }],
                }}
                selectionSettings={{
                  type: 'Single',
                  mode: 'Row',
                  enableToggle: false,
                }}
              />
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button size="md" className="button-secondary" onClick={handleReset}>
          Reset
        </Button>
        <Button
          size="md"
          className="button-primary"
          onClick={() => selectedPatient && confirmSelection(selectedPatient)}
          disabled={!selectedPatient}
        >
          Select
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AdvancePatientSearch
